import { useCallback, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'
import { refreshTokenRequest, API } from 'src/services/config'
import { STORAGE_KEYS, RENEW_TOKEN_BEFORE_EXPIRY } from './constants'
import { isTokenValid as checkTokenValid } from './storage.utils'

interface UseTokenRefreshProps {
  onTokenExpired?: () => void
  onTokenRefreshed?: () => void
}

interface UseTokenRefreshReturn {
  scheduleTokenRefresh: () => void
  cancelTokenRefresh: () => void
  renewToken: () => Promise<void>
  isTokenValid: () => boolean
}

/**
 * Hook customizado para gerenciar a renovação inteligente de tokens
 */
export function useTokenRefresh(
  { onTokenExpired, onTokenRefreshed }: UseTokenRefreshProps = {}
): UseTokenRefreshReturn {
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Renova o token usando o refresh token
   */
  const renewToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

    if (!storedRefreshToken) {
      console.warn('Refresh token não encontrado')
      onTokenExpired?.()
      return
    }

    try {
      const response = await refreshTokenRequest(storedRefreshToken)
      const { authenticationResult } = response
      const { accessToken, refreshToken: newRefreshToken, expiresIn } = authenticationResult

      const newExpiresToken = Date.now() + parseInt(expiresIn) * 1000

      // Atualizar tokens no localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken)
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
      localStorage.setItem(STORAGE_KEYS.EXPIRES_IN, newExpiresToken.toString())

      // Atualizar header de autorização
      API.defaults.headers['Authorization'] = `Bearer ${accessToken}`

      onTokenRefreshed?.()

      // Agendar próxima renovação
      scheduleTokenRefresh()
    } catch (error) {
      console.error('Erro ao renovar token:', error)
      toast.info('Sua sessão expirou. Faça o login novamente.')
      onTokenExpired?.()
    }
  }, [onTokenExpired, onTokenRefreshed])

  /**
   * Agenda a renovação do token no momento ideal
   */
  const scheduleTokenRefresh = useCallback(() => {
    // Limpar timeout anterior se existir
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }

    const expiresIn = localStorage.getItem(STORAGE_KEYS.EXPIRES_IN)
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

    if (!expiresIn || !refreshToken) {
      return
    }

    const tokenExpiry = parseInt(expiresIn)
    const now = Date.now()
    const timeUntilExpiry = tokenExpiry - now

    // Token já expirado
    if (timeUntilExpiry <= 0) {
      onTokenExpired?.()
      return
    }

    // Calcula quando deve renovar: 5 minutos antes da expiração
    const timeUntilRenewal = Math.max(0, timeUntilExpiry - RENEW_TOKEN_BEFORE_EXPIRY)

    // Agenda a renovação para o momento ideal
    refreshTimeoutRef.current = setTimeout(() => {
      renewToken()
    }, timeUntilRenewal)
  }, [renewToken, onTokenExpired])

  /**
   * Cancela a renovação agendada
   */
  const cancelTokenRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }
  }, [])

  /**
   * Verifica se o token ainda é válido
   */
  const isTokenValid = useCallback(() => {
    return checkTokenValid()
  }, [])

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      cancelTokenRefresh()
    }
  }, [cancelTokenRefresh])

  return {
    scheduleTokenRefresh,
    cancelTokenRefresh,
    renewToken,
    isTokenValid
  }
}
